import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Table,
  Input,
  Button,
} from "reactstrap";
import { updateImpactedPart } from "./service";

const cutlistColumns = [
  { key: "material", label: "Material", editable: true },
  { key: "board_name", label: "Board Name", editable: false },
  { key: "fb_name", label: "FB Name", editable: false },
  { key: "thickness", label: "Thickness", editable: false },
  { key: "fb_length", label: "FB Length", editable: true },
  { key: "fb_width", label: "FB Width", editable: true },
  { key: "quantity", label: "Quantity", editable: false },
  { key: "texture_direction", label: "Texture Direction", editable: false },
  { key: "left_edge_band_amount", label: "Left Edge Band Amount", editable: false },
  { key: "right_edge_band_amount", label: "Right Edge Band Amount", editable: false },
  { key: "top_edge_band_amount", label: "Top Edge Band Amount", editable: false },
  { key: "bottom_edge_band_amount", label: "Bottom Edge Band Amount", editable: false },
  { key: "cabinet_name", label: "Cabinet Name", editable: false },
  { key: "section_name", label: "Section Name", editable: false },
  { key: "customer", label: "Customer", editable: false },
  { key: "remark", label: "Remark", editable: true },
  { key: "room_name", label: "Room Name", editable: false },
  { key: "assembly_label", label: "Assembly Label", editable: true },
];

// Export download function for use outside modal
export const downloadCutlistCSV = (parts) => {
  const getCellValue = (part, fieldKey) => {
    if (!part.panel) return "-";
    return part.panel[fieldKey] || "-";
  };

  // Create CSV content
  const headers = ["#", ...cutlistColumns.map((col) => col.label)].join(",");
  const rows = parts.map((part, index) => {
    const rowData = [
      index + 1,
      ...cutlistColumns.map((col) => {
        const value = getCellValue(part, col.key);
        // Escape commas and quotes in CSV
        const escapedValue = String(value).replace(/"/g, '""');
        return `"${escapedValue}"`;
      }),
    ];
    return rowData.join(",");
  });

  const csvContent = [headers, ...rows].join("\n");

  // Create blob and download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `cutlist_${new Date().toISOString().split("T")[0]}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const CutlistModal = ({ isOpen, toggle, parts, ticketStatus, onUpdate }) => {
  const [editingCell, setEditingCell] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(null);
  // Local cache of panel values by partId to avoid reverting previous edits while parent refresh is in-flight
  const [panelCacheByPartId, setPanelCacheByPartId] = useState({});

  useEffect(() => {
    // Reset transient state when closing the modal
    if (!isOpen) {
      setEditingCell(null);
      setEditData({});
      setSaving(null);
      setPanelCacheByPartId({});
    }
  }, [isOpen]);

  // Editable fields (marked with *)
  const editableFields = [
    "material",
    "fb_length",
    "fb_width",
    "remark",
    "assembly_label",
  ];

  // Use exported columns
  const columns = cutlistColumns;

  const canEdit = ticketStatus === "open";

  const handleCellClick = (partId, fieldKey) => {
    if (!canEdit || !editableFields.includes(fieldKey)) return;

    const part = parts.find((p) => p.id === partId);
    if (!part || !part.panel) return;

    const currentPanel = panelCacheByPartId[partId] || part.panel || {};
    setEditingCell(`${partId}-${fieldKey}`);
    setEditData({
      partId,
      fieldKey,
      value: currentPanel[fieldKey] || "",
    });
  };

  const handleCellBlur = async () => {
    if (!editingCell) return;

    const { partId, fieldKey, value } = editData;
    const part = parts.find((p) => p.id === partId);

    if (!part || !part.panel) {
      setEditingCell(null);
      setEditData({});
      return;
    }

    const basePanel = panelCacheByPartId[partId] || part.panel || {};
    const originalValue = basePanel[fieldKey] || "";
    const newValue = value || "";

    // Only save if value changed
    if (originalValue !== newValue) {
      try {
        setSaving(`${partId}-${fieldKey}`);
        const formData = new FormData();
        // Backend treats `panel` as a whole (JSON) in some flows; sending only one key can overwrite others.
        // Always send the full merged panel object, using our local cache to avoid racey “revert” behavior.
        const nextPanel = { ...basePanel, [fieldKey]: newValue };
        Object.keys(nextPanel).forEach((k) => {
          const v = nextPanel[k];
          if (v === undefined || v === null) return;
          if (typeof v === "object") {
            formData.append(`panel[${k}]`, JSON.stringify(v));
          } else {
            formData.append(`panel[${k}]`, String(v));
          }
        });

        await updateImpactedPart(partId, formData);

        setPanelCacheByPartId((prev) => ({
          ...prev,
          [partId]: { ...basePanel, [fieldKey]: newValue },
        }));

        if (onUpdate) {
          onUpdate();
        }
      } catch (err) {
        console.error("Failed to update panel field:", err);
        alert("Failed to update field: " + (err.message || "Unknown error"));
      } finally {
        setSaving(null);
      }
    }

    setEditingCell(null);
    setEditData({});
  };

  const handleCellChange = (e) => {
    setEditData({
      ...editData,
      value: e.target.value,
    });
  };

  const getCellValue = (part, fieldKey) => {
    if (!part.panel) return "-";
    return part.panel[fieldKey] || "-";
  };

  const handleDownloadCSV = () => {
    downloadCutlistCSV(parts);
  };

  const renderCell = (part, column) => {
    const cellId = `${part.id}-${column.key}`;
    const isEditing = editingCell === cellId;
    const isSaving = saving === cellId;
    const isEditable = canEdit && column.editable;

    if (isEditing) {
      return (
        <Input
          type="text"
          value={editData.value}
          onChange={handleCellChange}
          onBlur={handleCellBlur}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              handleCellBlur();
            }
          }}
          autoFocus
          bsSize="sm"
        />
      );
    }

    const value = getCellValue(part, column.key);
    const cellContent = (
      <div
        onClick={() => handleCellClick(part.id, column.key)}
        style={{
          cursor: isEditable ? "pointer" : "default",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          padding: "4px",
        }}
        className={isEditable ? "text-decoration-underline" : ""}
      >
        {isSaving ? (
          <span className="text-muted">
            <i className="fas fa-spinner fa-spin me-1"></i>Saving...
          </span>
        ) : (
          <>
            <span style={{ flex: 1 }}>{value}</span>
            {isEditable && (
              <i
                className="fas fa-edit text-muted"
                style={{ fontSize: "0.8rem" }}
              ></i>
            )}
          </>
        )}
      </div>
    );

    return cellContent;
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      style={{ maxWidth: "95vw" }}
    >
      <ModalHeader toggle={toggle}>
        <h5 className="mb-0 fw-bold">Cutlist - Panel Details</h5>
      </ModalHeader>
      <ModalBody style={{ maxHeight: "80vh", overflowY: "auto" }}>
        {!parts || parts.length === 0 ? (
          <div className="text-center p-4">
            <p className="text-muted">No parts available</p>
          </div>
        ) : (
          <Table bordered hover responsive size="sm" className="align-middle">
            <thead
              className="table-light"
              style={{ position: "sticky", top: 0, zIndex: 10 }}
            >
              <tr>
                <th style={{ width: "5%" }}>#</th>
                {columns.map((column) => (
                  <th key={column.key} style={{ whiteSpace: "nowrap" }}>
                    {column.label}
                    {column.editable && (
                      <span className="text-danger ms-1">*</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {parts.map((part, index) => {
                // Maintain same order as ImpactedPartsTable, show all parts even if panel is missing
                return (
                  <tr key={part.id}>
                    <td>{index + 1}</td>
                    {columns.map((column) => (
                      <td key={column.key} style={{ minWidth: "120px" }}>
                        {part.panel ? renderCell(part, column) : "-"}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
        {canEdit && (
          <div className="mt-3 text-muted small">
            <i className="fas fa-info-circle me-1"></i>
            Click on fields marked with <span className="text-danger">
              *
            </span>{" "}
            to edit
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default CutlistModal;
